import React, { useState, useEffect, useMemo } from 'react';
import { resolveElementStyle, buildPanelStyle, buildBadgeStyle, appendCacheBuster } from '../../utils/mobileStyleUtils';

const API_BASE = process.env.REACT_APP_API_BASE || (typeof window !== 'undefined' && window.location.port === '3000' ? `http://${window.location.hostname}:8000` : '');

export const SAMPLE_OPERATOR_METRICS = {
  unallocated_coins: 9,
  allocated_coins: 3,
  total_coins: 12,
  total_views: 29,
  total_shares: 28,
  total_host_clicks: 7,
  total_booking_clicks: 2,
  total_website_clicks: 3,
  total_email_clicks: 2,
  top_cards: [
    {
      id: 1,
      public_code: 'TC-88219-FT',
      coin_number: '001',
      card_data: { species: 'Trophy Largemouth Bass', title: 'Trophy Largemouth Bass (8.4 lbs)' },
      card_name: 'Trophy Largemouth Bass',
      card_photo_url: '/uploads/1788594344-WhatsAppImage2026-08-04at06.24.59.jpeg',
      preview_front_url: '/uploads/1788594344-WhatsAppImage2026-08-04at06.24.59.jpeg',
      views_count: 10,
      shares_count: 23,
      host_clicks_count: 3,
      is_issued: true
    },
    {
      id: 2,
      public_code: 'TC-99401-FT',
      coin_number: '002',
      card_data: { species: 'Trophy Striped Bass', title: 'Striped Bass (38")' },
      card_name: 'Trophy Striped Bass',
      card_photo_url: '/uploads/1788594338-WhatsAppImage2026-08-05at03.49.58.jpeg',
      preview_front_url: '/uploads/1788594338-WhatsAppImage2026-08-05at03.49.58.jpeg',
      views_count: 16,
      shares_count: 3,
      host_clicks_count: 2,
      is_issued: true
    },
    {
      id: 3,
      public_code: 'TC-10492-FT',
      coin_number: '003',
      card_data: { species: 'Highland Rainbow Trout', title: 'Rainbow Trout (5.2 lbs)' },
      card_name: 'Highland Rainbow Trout',
      card_photo_url: '/uploads/1788594332-WhatsAppImage2026-08-08at05.59.46.jpeg',
      preview_front_url: '/uploads/1788594332-WhatsAppImage2026-08-08at05.59.46.jpeg',
      views_count: 3,
      shares_count: 2,
      host_clicks_count: 2,
      is_issued: true
    }
  ]
};

export default function MobileOperatorDashboard({
  user,
  tenant,
  onSelectCard,
  onNavigateToVault,
  mobileStyles,
  onElementClick,
  selectedElement,
  overrideMetrics,
  cacheBusterKey = null
}) {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    unallocated_coins: 0,
    allocated_coins: 0,
    total_coins: 0,
    total_views: 0,
    total_shares: 0,
    total_host_clicks: 0,
    total_booking_clicks: 0,
    total_website_clicks: 0,
    total_email_clicks: 0,
    top_cards: []
  });
  const [error, setError] = useState('');

  const operatorName = user?.first_name || user?.nickname || (user?.email ? user.email.split('@')[0] : 'Operator');

  const resolvedPanels = useMemo(() => resolveElementStyle(mobileStyles, 'operator:home', 'content_panels'), [mobileStyles]);
  const resolvedBadges = useMemo(() => resolveElementStyle(mobileStyles, 'operator:home', 'badges'), [mobileStyles]);
  const resolvedTypo = useMemo(() => resolveElementStyle(mobileStyles, 'operator:home', 'typography'), [mobileStyles]);

  const customPanelStyle = useMemo(() => buildPanelStyle(resolvedPanels.style), [resolvedPanels]);
  const customBadgeStyle = useMemo(() => buildBadgeStyle(resolvedBadges.style), [resolvedBadges]);
  const typoConfig = resolvedTypo.style || {};

  const activeMetrics = useMemo(() => {
    if (overrideMetrics) return overrideMetrics;
    if (metrics.top_cards && metrics.top_cards.length > 0) return metrics;
    if (!loading && metrics.top_cards.length === 0) return SAMPLE_OPERATOR_METRICS;
    return metrics;
  }, [overrideMetrics, metrics, loading]);

  const getInteractive = (elementKey) => {
    if (!onElementClick) return {};
    const isSelected = selectedElement === elementKey;
    return {
      onClick: (e) => {
        e.stopPropagation();
        onElementClick(elementKey);
      },
      style: {
        cursor: 'pointer',
        outline: isSelected ? '2px solid #38bdf8' : 'none',
        outlineOffset: '2px',
        transition: 'outline 0.15s ease'
      }
    };
  };

  useEffect(() => {
    const fetchOperatorData = async () => {
      setLoading(true);
      setError('');
      try {
        const token = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
        // Fetch vault data with engagement sorting to extract KPIs and top 3 performing cards
        const res = await fetch(`${API_BASE}/api/v1/coin-vault/vault?status=all&sort_by=views&limit=50&summary=true`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'X-Tenant-Domain': typeof window !== 'undefined' ? window.location.hostname : ''
          }
        });
        if (!res.ok) throw new Error('Failed to load operator metrics');
        const data = await res.json();

        // Extract allocated cards sorted by engagement (views + shares*2 + clicks*3)
        const allCoins = data?.coins || [];
        const allocatedCoins = allCoins.filter(c => c.is_issued);

        allocatedCoins.sort((a, b) => {
          const scoreA = (a.views_count || 0) + (a.shares_count || 0) * 2 + (a.host_clicks_count || 0) * 3;
          const scoreB = (b.views_count || 0) + (b.shares_count || 0) * 2 + (b.host_clicks_count || 0) * 3;
          return scoreB - scoreA;
        });

        const topThree = allocatedCoins.slice(0, 3);

        setMetrics({
          unallocated_coins: data.unallocated_coins || 0,
          allocated_coins: data.allocated_coins || (allocatedCoins.length),
          total_coins: data.total_coins || (data.allocated_coins + data.unallocated_coins) || 0,
          total_views: data.total_views || 0,
          total_shares: data.total_shares || 0,
          total_host_clicks: data.total_host_clicks || 0,
          total_booking_clicks: data.total_booking_clicks || 0,
          total_website_clicks: data.total_website_clicks || 0,
          total_email_clicks: data.total_email_clicks || 0,
          top_cards: topThree
        });
      } catch (err) {
        console.error('[MobileOperatorDashboard] Error:', err);
        setError(err.message || 'Unable to refresh metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchOperatorData();
  }, []);

  return (
    <div
      data-testid="mobile-operator-dashboard"
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        padding: '16px 16px 90px 16px',
        boxSizing: 'border-box',
        gap: '18px',
        fontFamily: typoConfig.font_family
      }}
    >
      {/* Operator Welcome Banner / Hub Card */}
      <div
        {...getInteractive('content_panels')}
        data-testid="operator-welcome-card"
        style={{
          ...customPanelStyle,
          padding: '16px 18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          ...getInteractive('content_panels').style
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              {...getInteractive('badges')}
              data-testid="operator-hub-badge"
              style={{
                ...customBadgeStyle,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '3px 8px',
                textTransform: 'uppercase',
                letterSpacing: `${resolvedBadges.style?.letter_spacing ?? 0.6}px`,
                ...getInteractive('badges').style
              }}
            >
              ⚡ Operator Hub
            </span>
          </div>
          <h2
            {...getInteractive('typography')}
            style={{
              fontSize: '18px',
              fontWeight: 800,
              color: typoConfig.heading_color || '#f8fafc',
              fontFamily: typoConfig.font_family,
              margin: '6px 0 2px 0',
              letterSpacing: `${typoConfig.letter_spacing ?? -0.3}px`,
              ...getInteractive('typography').style
            }}
          >
            Hello, {operatorName}
          </h2>
          <p style={{
            fontSize: '11px',
            color: typoConfig.body_color || '#94a3b8',
            fontFamily: typoConfig.font_family,
            margin: 0
          }}>
            {tenant?.brand_name || 'TrophyCoin Operator'}
          </p>
        </div>

        <div style={{
          width: '42px',
          height: '42px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff',
          boxShadow: '0 4px 12px rgba(2, 132, 199, 0.35)'
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.25)',
          borderRadius: '12px',
          padding: '10px 14px',
          color: '#f87171',
          fontSize: '11px'
        }}>
          {error}
        </div>
      )}

      {/* Primary Status Counters (Unallocated Remaining & Coins Minted to Date) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '12px'
      }}>
        {/* Unallocated Coins Remaining */}
        <div
          {...getInteractive('content_panels')}
          data-testid="stat-unallocated-coins"
          style={{
            ...customPanelStyle,
            padding: '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            ...getInteractive('content_panels').style
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: typoConfig.accent_color || '#f59e0b', fontSize: '11px', fontWeight: 700, fontFamily: typoConfig.font_family }}>
            <span>Unallocated Coins</span>
            <span style={{ fontSize: '14px' }}>🪙</span>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: typoConfig.heading_color || '#fef3c7', letterSpacing: '-0.5px', fontFamily: typoConfig.font_family }}>
            {loading && !overrideMetrics ? '...' : activeMetrics.unallocated_coins}
          </div>
          <div style={{ fontSize: '10px', color: typoConfig.accent_color || '#fbbf24', opacity: 0.9, fontFamily: typoConfig.font_family }}>
            Ready to issue to collectors
          </div>
        </div>

        {/* Coins Minted to Date */}
        <div
          {...getInteractive('content_panels')}
          data-testid="stat-minted-coins"
          style={{
            ...customPanelStyle,
            padding: '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            ...getInteractive('content_panels').style
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#34d399', fontSize: '11px', fontWeight: 700, fontFamily: typoConfig.font_family }}>
            <span>Minted to Date</span>
            <span style={{ fontSize: '14px' }}>🏆</span>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: typoConfig.heading_color || '#ecfdf5', letterSpacing: '-0.5px', fontFamily: typoConfig.font_family }}>
            {loading && !overrideMetrics ? '...' : activeMetrics.allocated_coins}
          </div>
          <div style={{ fontSize: '10px', color: '#6ee7b7', opacity: 0.9, fontFamily: typoConfig.font_family }}>
            Total allocated cards
          </div>
        </div>
      </div>

      {/* Engagement & Click-Through Stats */}
      <div
        {...getInteractive('content_panels')}
        data-testid="collector-engagement-panel"
        style={{
          ...customPanelStyle,
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          ...getInteractive('content_panels').style
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '12px', fontWeight: 700, color: typoConfig.heading_color || '#f8fafc', textTransform: 'uppercase', letterSpacing: '0.4px', fontFamily: typoConfig.font_family }}>
            Collector Engagement & Traffic
          </span>
          <span style={{ fontSize: '10px', color: typoConfig.body_color || '#94a3b8', fontFamily: typoConfig.font_family }}>All-Time</span>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '8px',
          textAlign: 'center'
        }}>
          {/* Card Views */}
          <div
            data-testid="stat-card-views"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '12px',
              padding: '10px 8px',
              border: '1px solid rgba(255, 255, 255, 0.06)'
            }}
          >
            <div style={{ fontSize: '10px', color: typoConfig.body_color || '#94a3b8', marginBottom: '2px', fontWeight: 600, fontFamily: typoConfig.font_family }}>
              Card Views
            </div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#38bdf8', fontFamily: typoConfig.font_family }}>
              {loading && !overrideMetrics ? '...' : (activeMetrics.total_views ?? 0).toLocaleString()}
            </div>
            <div style={{ fontSize: '9px', color: typoConfig.body_color || '#64748b', marginTop: '2px', fontFamily: typoConfig.font_family }}>
              Scans & opens
            </div>
          </div>

          {/* Card Shares */}
          <div
            data-testid="stat-card-shares"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '12px',
              padding: '10px 8px',
              border: '1px solid rgba(255, 255, 255, 0.06)'
            }}
          >
            <div style={{ fontSize: '10px', color: typoConfig.body_color || '#94a3b8', marginBottom: '2px', fontWeight: 600, fontFamily: typoConfig.font_family }}>
              Card Shares
            </div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#c084fc', fontFamily: typoConfig.font_family }}>
              {loading && !overrideMetrics ? '...' : (activeMetrics.total_shares ?? 0).toLocaleString()}
            </div>
            <div style={{ fontSize: '9px', color: typoConfig.body_color || '#64748b', marginTop: '2px', fontFamily: typoConfig.font_family }}>
              Social forwards
            </div>
          </div>

          {/* Click-Throughs / Referrals */}
          <div
            data-testid="stat-click-throughs"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '12px',
              padding: '10px 8px',
              border: '1px solid rgba(255, 255, 255, 0.06)'
            }}
          >
            <div style={{ fontSize: '10px', color: typoConfig.body_color || '#94a3b8', marginBottom: '2px', fontWeight: 600, fontFamily: typoConfig.font_family }}>
              Click-Throughs
            </div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#34d399', fontFamily: typoConfig.font_family }}>
              {loading && !overrideMetrics ? '...' : (activeMetrics.total_host_clicks ?? 0).toLocaleString()}
            </div>
            <div style={{ fontSize: '9px', color: typoConfig.body_color || '#64748b', marginTop: '2px', fontFamily: typoConfig.font_family }}>
              Web & booking
            </div>
          </div>
        </div>

        {/* Click Breakdown Pill Bar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '10.5px',
          color: typoConfig.body_color || '#94a3b8',
          borderTop: '1px solid rgba(255, 255, 255, 0.06)',
          paddingTop: '8px',
          fontFamily: typoConfig.font_family
        }}>
          <span>🌐 Website: <strong style={{ color: typoConfig.heading_color || '#f8fafc' }}>{activeMetrics.total_website_clicks ?? 0}</strong></span>
          <span>📅 Booking: <strong style={{ color: typoConfig.heading_color || '#f8fafc' }}>{activeMetrics.total_booking_clicks ?? 0}</strong></span>
          <span>✉️ Email: <strong style={{ color: typoConfig.heading_color || '#f8fafc' }}>{activeMetrics.total_email_clicks ?? 0}</strong></span>
        </div>
      </div>

      {/* TOP THREE PERFORMING CARDS SECTION */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '14px' }}>🔥</span>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 800, color: typoConfig.heading_color || '#f8fafc', letterSpacing: '-0.2px', fontFamily: typoConfig.font_family }}>
                Top Performing Cards
              </h3>
            </div>
            <p style={{ margin: '2px 0 0 0', fontSize: '10.5px', color: typoConfig.body_color || '#94a3b8', fontFamily: typoConfig.font_family }}>
              Ranked by collector views, shares, and referrals
            </p>
          </div>

          {onNavigateToVault && (
            <button
              type="button"
              onClick={onNavigateToVault}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#38bdf8',
                fontSize: '11px',
                fontWeight: 700,
                cursor: 'pointer',
                padding: '4px 0',
                fontFamily: typoConfig.font_family
              }}
            >
              View Vault &rarr;
            </button>
          )}
        </div>

        {loading && !overrideMetrics ? (
          <div style={{ padding: '24px', textAlign: 'center', color: typoConfig.body_color || '#64748b', fontSize: '12px' }}>
            Loading top cards...
          </div>
        ) : activeMetrics.top_cards.length === 0 ? (
          <div style={{
            ...customPanelStyle,
            padding: '24px',
            textAlign: 'center',
            color: typoConfig.body_color || '#94a3b8',
            fontSize: '12px'
          }}>
            No allocated cards found yet. As you issue coins to collectors, the highest performing cards will rank here!
          </div>
        ) : (
          /* Neat Row of Top 3 Cards with Rank Overlay */
          <div
            data-testid="top-performing-cards-row"
            style={{
              display: 'flex',
              gap: '10px',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            {activeMetrics.top_cards.map((coin, idx) => {
              const rank = idx + 1;
              const cardTitle = coin.card_data?.species || coin.card_data?.title || coin.card_name || `Card #${coin.coin_number}`;
              const views = coin.views_count || 0;
              const shares = coin.shares_count || 0;

              return (
                <div
                  key={coin.id || idx}
                  data-testid={`top-card-item-${rank}`}
                  {...getInteractive('content_panels')}
                  onClick={() => onSelectCard && onSelectCard(coin, { openStats: true })}
                  style={{
                    ...customPanelStyle,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 6px',
                    transition: 'transform 0.15s ease, border-color 0.15s ease',
                    boxSizing: 'border-box',
                    ...getInteractive('content_panels').style
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Card Preview Container with Rank Badge Overlay */}
                  <div style={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '350 / 490',
                    borderRadius: `${Math.max(4, (resolvedPanels.style?.border_radius || 16) - 6)}px`,
                    overflow: 'hidden',
                    backgroundColor: '#0a0e17',
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.45)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {coin.preview_front_url || coin.card_photo_url ? (
                      <img
                        src={cacheBusterKey ? appendCacheBuster(coin.preview_front_url || coin.card_photo_url, cacheBusterKey) : (coin.preview_front_url || coin.card_photo_url)}
                        alt={cardTitle}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                    ) : (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#64748b',
                        padding: '6px'
                      }}>
                        <span style={{ fontSize: '20px' }}>🏆</span>
                        <span style={{ fontSize: '9px', marginTop: '2px', color: '#cbd5e1' }}>#{coin.coin_number}</span>
                      </div>
                    )}

                    {/* OVERLAY RANK BADGE OVER THE PREVIEW OF THE CARD */}
                    <div
                      data-testid={`rank-badge-${rank}`}
                      {...getInteractive('badges')}
                      style={{
                        position: 'absolute',
                        top: '5px',
                        left: '5px',
                        ...customBadgeStyle,
                        padding: '2px 6px',
                        fontSize: '10px',
                        fontWeight: 900,
                        letterSpacing: '-0.3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                        ...getInteractive('badges').style
                      }}
                    >
                      #{rank}
                    </div>
                  </div>

                  {/* Card title & stats */}
                  <div style={{
                    width: '100%',
                    marginTop: '6px',
                    textAlign: 'center',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      fontSize: '10.5px',
                      fontWeight: 700,
                      color: typoConfig.heading_color || '#f8fafc',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontFamily: typoConfig.font_family
                    }}>
                      {cardTitle}
                    </div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      marginTop: '2px',
                      fontSize: '9px',
                      color: typoConfig.body_color || '#94a3b8',
                      fontFamily: typoConfig.font_family
                    }}>
                      <span>👁️ {views}</span>
                      <span>↗️ {shares}</span>
                    </div>

                    <div style={{
                      marginTop: '4px',
                      fontSize: '8.5px',
                      color: '#38bdf8',
                      fontWeight: 600,
                      backgroundColor: 'rgba(56, 189, 248, 0.1)',
                      padding: '2px 4px',
                      borderRadius: '4px',
                      fontFamily: typoConfig.font_family
                    }}>
                      View Stats &rarr;
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Action: Open Vault */}
      {onNavigateToVault && (
        <button
          type="button"
          onClick={onNavigateToVault}
          style={{
            width: '100%',
            ...customPanelStyle,
            color: typoConfig.heading_color || '#f8fafc',
            padding: '12px 16px',
            fontSize: '12.5px',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
            transition: 'all 0.2s ease',
            fontFamily: typoConfig.font_family
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="12" cy="12" r="5" />
            <path d="M12 7v2" /><path d="M12 15v2" /><path d="M7 12h2" /><path d="M15 12h2" />
          </svg>
          <span>Explore All Allocated Cards in Vault</span>
        </button>
      )}
    </div>
  );
}
